import React, { useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography
} from '@mui/material';
import { UserNft } from '../types';
import { PriceCalculator } from './PriceCalculator';
import { MONO, Pill, rarityHex, cyanBtnSx } from './cardKit';
import { cardTitle } from '../../NFTCollection/components/CardInspectModal';

type Props = {
  open: boolean;
  onClose: () => void;
  selectedNft: UserNft | null;

  listingPrice: string;
  setListingPrice: (v: string) => void;

  listingQuantity: number;
  setListingQuantity: (n: number) => void;

  quantityInput: string;
  setQuantityInput: (v: string) => void;

  isProcessing: boolean;

  showPriceCalculator: boolean;
  togglePriceCalculator: () => void;

  loadingLastPrice: boolean;
  lastSoldPrice: {
    hasLastSale: boolean;
    lastSoldPrice?: string;
    message?: string;
  } | null;

  formatRarity: (rarity: string | undefined | null) => string;

  onPayFeeUsingEarnedTokens: () => void;
  onPayFeeUsingInternalWallet: () => void;
  onPayFeeUsingConnectedWallet: () => void;

  userIncBalance: string;
  userEarnedIncBalance: string;

  hasConnectedWallet: boolean;

  get: (url: string, auth?: boolean) => Promise<any>;
};

const toNum = (v: string) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const paperSx = {
  borderRadius: '16px',
  border: '1px solid rgba(56, 232, 255, 0.28)',
  // Set colour + image explicitly (not the `background` shorthand) so MUI's
  // dark elevation overlay is replaced, not left transparent.
  backgroundColor: '#0b1119',
  backgroundImage: 'linear-gradient(180deg, #0b1119, #070b11)',
  boxShadow: '0 30px 80px -20px rgba(0, 0, 0, 0.85)'
} as const;

const payOutlineSx = {
  fontWeight: 800,
  color: '#38e8ff',
  borderColor: 'rgba(56, 232, 255, 0.5)',
  borderRadius: '9px',
  py: 1,
  '&:hover': {
    borderColor: '#38e8ff',
    backgroundColor: 'rgba(56, 232, 255, 0.08)'
  },
  '&.Mui-disabled': {
    color: 'rgba(140, 170, 190, 0.35)',
    borderColor: 'rgba(140, 170, 190, 0.18)'
  }
} as const;

/* On a phone side by side only if both balances fit, stacked otherwise; from
 * sm up they share the row equally, as before. The unit may drop under the
 * number and a number that cannot fit even alone breaks rather than spill
 * into the next chip. The same chip as PurchaseNftModal's — keep the two alike. */
const BalanceChip = ({ label, value }: { label: string; value: string }) => (
  <Box
    sx={{
      flex: { xs: '1 1 auto', sm: 1 },
      minWidth: 0,
      p: 1.25,
      borderRadius: '10px',
      border: '1px solid rgba(140, 170, 190, 0.16)',
      background: 'rgba(7, 11, 17, 0.5)'
    }}
  >
    <Typography
      sx={{
        font: `800 9px ${MONO}`,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: '#8ea3b3'
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        mt: 0.4,
        font: `900 15px ${MONO}`,
        color: '#e8f4fb',
        fontVariantNumeric: 'tabular-nums',
        overflowWrap: 'anywhere'
      }}
    >
      {value}
      <wbr />
      <Box component="span" sx={{ ml: 0.5, fontSize: 10, color: '#5c6b78' }}>
        INC
      </Box>
    </Typography>
  </Box>
);

const FeeRow = ({
  label,
  value,
  strong = false
}: {
  label: string;
  value: string;
  strong?: boolean;
}) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
    <Typography sx={{ font: `600 12px ${MONO}`, color: '#8ea3b3' }}>
      {label}
    </Typography>
    <Typography
      sx={{
        font: `${strong ? 800 : 600} 12px ${MONO}`,
        color: strong ? '#ffcf40' : '#e8f4fb',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap'
      }}
    >
      {value}
    </Typography>
  </Box>
);

export const ListNftModal: React.FC<Props> = ({
  open,
  onClose,
  selectedNft,
  listingPrice,
  setListingPrice,
  listingQuantity,
  setListingQuantity,
  quantityInput,
  setQuantityInput,
  isProcessing,
  showPriceCalculator,
  togglePriceCalculator,
  loadingLastPrice,
  lastSoldPrice,
  formatRarity,

  onPayFeeUsingEarnedTokens,
  onPayFeeUsingInternalWallet,
  onPayFeeUsingConnectedWallet,

  userIncBalance,
  userEarnedIncBalance,
  hasConnectedWallet,

  get
}) => {
  const priceNum = useMemo(() => toNum(listingPrice), [listingPrice]);
  const qty = useMemo(
    () => Math.max(1, listingQuantity || 1),
    [listingQuantity]
  );

  const feePerNft = useMemo(
    () => (priceNum > 0 ? priceNum * 0.05 : 0),
    [priceNum]
  );
  const totalFee = useMemo(() => feePerNft * qty, [feePerNft, qty]);
  const minPriceOk = useMemo(() => priceNum >= 10, [priceNum]);

  const internalBalNum = useMemo(() => toNum(userIncBalance), [userIncBalance]);
  const earnedBalNum = useMemo(
    () => toNum(userEarnedIncBalance),
    [userEarnedIncBalance]
  );

  const hasEnoughEarned = useMemo(
    () => earnedBalNum >= totalFee,
    [earnedBalNum, totalFee]
  );
  const hasEnoughInternal = useMemo(
    () => internalBalNum >= totalFee,
    [internalBalNum, totalFee]
  );

  const qtyValid = useMemo(() => {
    if (!selectedNft) return false;
    const maxAllowed = Math.min(selectedNft.owned - (selectedNft.locked || 0), 10);
    return qty >= 1 && qty <= maxAllowed;
  }, [qty, selectedNft]);

  const canSubmit = useMemo(() => {
    return !!selectedNft && minPriceOk && qtyValid && !isProcessing;
  }, [selectedNft, minPriceOk, qtyValid, isProcessing]);

  const rc = rarityHex(selectedNft?.nft.rarity);
  const busy = (label: string) =>
    isProcessing ? <CircularProgress size={20} color="inherit" /> : label;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{ sx: paperSx }}
    >
      <DialogContent sx={{ p: 3 }}>
        <Typography
          sx={{
            font: `800 10px ${MONO}`,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: '#8ea3b3',
            mb: 2
          }}
        >
          List for sale
        </Typography>

        {selectedNft && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2.5 }}>
            <Box
              sx={{
                width: 84,
                height: 84,
                flexShrink: 0,
                borderRadius: '12px',
                display: 'grid',
                placeItems: 'center',
                background:
                  'radial-gradient(120% 100% at 50% 30%, #1b3350, #0a0f16 74%)',
                border: '1px solid rgba(56, 232, 255, 0.16)'
              }}
            >
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: '50%',
                  background: `radial-gradient(circle at 42% 38%, ${rc}, #0b2740 72%)`,
                  boxShadow: `0 0 24px ${rc}55`
                }}
              />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontWeight: 800, fontSize: 18, color: '#e8f4fb' }}
              >
                {cardTitle(selectedNft.nft.name)}
              </Typography>
              {/* May wrap, as in PurchaseNftModal: beside the art a narrow
                  phone has no room for the pill and "INFLUENCER · YOU OWN 12"
                  on one line, and the dialog's content scrolled sideways. */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignItems: 'center',
                  mt: 1
                }}
              >
                <Pill color={rc}>{formatRarity(selectedNft.nft.rarity)}</Pill>
                <Typography
                  sx={{
                    font: `700 10px ${MONO}`,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: '#8ea3b3'
                  }}
                >
                  {selectedNft.nft.type} · you own {selectedNft.owned}
                  {(selectedNft.locked || 0) > 0 &&
                    ` · ${selectedNft.locked} from your free starter pack ${selectedNft.locked === 1 ? 'is' : 'are'} locked until you verify your identity or spend A$99`}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {(loadingLastPrice || lastSoldPrice) && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: '10px',
              border: '1px solid rgba(56, 232, 255, 0.18)',
              background: 'rgba(56, 232, 255, 0.05)'
            }}
          >
            {loadingLastPrice ? (
              <Typography sx={{ font: `600 12px ${MONO}`, color: '#38e8ff' }}>
                Loading price history…
              </Typography>
            ) : lastSoldPrice?.hasLastSale ? (
              <Typography sx={{ font: `700 13px ${MONO}`, color: '#38e8ff' }}>
                Last sold for {lastSoldPrice.lastSoldPrice} INC
              </Typography>
            ) : (
              <Typography sx={{ font: `600 12px ${MONO}`, color: '#8ea3b3' }}>
                {lastSoldPrice?.message || 'No previous sales found'}
              </Typography>
            )}
          </Box>
        )}

        <PriceCalculator
          incAmount={priceNum || 0}
          onIncAmountChange={(amount) => setListingPrice(amount.toString())}
          showCalculator={showPriceCalculator}
          onToggleCalculator={togglePriceCalculator}
          get={get}
        />

        {selectedNft && selectedNft.owned > 1 && (
          <TextField
            label="Quantity to list"
            type="number"
            fullWidth
            value={quantityInput}
            onChange={(e) => setQuantityInput(e.target.value)}
            onBlur={(e) => {
              const inputValue = parseInt(e.target.value) || 1;
              const maxAllowed = Math.min(selectedNft.owned - (selectedNft.locked || 0), 10);
              const validatedQty = Math.max(1, Math.min(maxAllowed, inputValue));
              setListingQuantity(validatedQty);
              setQuantityInput(validatedQty.toString());
            }}
            inputProps={{
              min: 1,
              max: Math.min(selectedNft.owned - (selectedNft.locked || 0), 10),
              step: 1
            }}
            helperText={`List 1–${Math.min(
              selectedNft.owned,
              10
            )} at once (max 10 per batch)`}
            disabled={isProcessing}
            error={
              quantityInput !== '' &&
              (isNaN(parseInt(quantityInput)) ||
                parseInt(quantityInput) < 1 ||
                parseInt(quantityInput) > Math.min(selectedNft.owned - (selectedNft.locked || 0), 10))
            }
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          label="Price per collectible (INC)"
          type="number"
          fullWidth
          value={listingPrice}
          onChange={(e) => setListingPrice(e.target.value)}
          helperText={
            qty > 1
              ? `Min 10 INC each · listing ${qty} at ${priceNum || 0} INC each`
              : `Minimum 10 INC.`
          }
          sx={{ mb: 2 }}
          disabled={isProcessing}
          error={listingPrice !== '' && !minPriceOk}
        />

        {/* Fee breakdown */}
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: '12px',
            border: '1px solid rgba(255, 207, 64, 0.22)',
            background: 'rgba(255, 207, 64, 0.05)'
          }}
        >
          <Typography
            sx={{
              font: `800 9px ${MONO}`,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: '#c8a233',
              mb: 1
            }}
          >
            Listing fee · 5%
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <FeeRow
              label={qty > 1 ? 'Fee per collectible' : 'Fee'}
              value={`${feePerNft.toFixed(5)} INC`}
            />
            {qty > 1 && (
              <FeeRow
                label={`Total fee · ${qty}`}
                value={`${totalFee.toFixed(5)} INC`}
                strong
              />
            )}
            {qty > 1 && (
              <FeeRow
                label="Revenue if all sell"
                value={`${(priceNum * qty).toFixed(5)} INC`}
              />
            )}
          </Box>
          <Typography
            sx={{
              mt: 1,
              font: `600 11px ${MONO}`,
              color: '#8a7526',
              fontStyle: 'italic'
            }}
          >
            This can&apos;t be undone.
            {qty > 1 ? ` Creates ${qty} separate listings.` : ''}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          <BalanceChip label="Earned tokens" value={userEarnedIncBalance} />
          <BalanceChip label="INC balance" value={userIncBalance} />
        </Box>

        {canSubmit && !hasEnoughEarned && (
          <Typography
            sx={{ mb: 1.5, font: `600 12px ${MONO}`, color: '#ff8a8a' }}
          >
            Not enough earned tokens for the fee ({earnedBalNum.toFixed(5)} of{' '}
            {totalFee.toFixed(5)} INC) — use another method below.
          </Typography>
        )}

        <Typography sx={{ font: `600 11px/1.6 ${MONO}`, color: '#5c6b78' }}>
          Paying the fee by anything other than earned tokens needs a little SOL
          for network fees.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, display: 'block' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={onPayFeeUsingEarnedTokens}
            disabled={!canSubmit || !hasEnoughEarned}
            sx={{ ...cyanBtnSx, '&.Mui-disabled': { opacity: 0.4 } }}
          >
            {busy('List — pay fee with Earned Tokens')}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={onPayFeeUsingInternalWallet}
            disabled={!canSubmit || !hasConnectedWallet || !hasEnoughInternal}
            sx={payOutlineSx}
          >
            {busy('List — pay fee with INC Tokens')}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={onPayFeeUsingConnectedWallet}
            disabled={!canSubmit || !hasConnectedWallet}
            sx={payOutlineSx}
          >
            {busy('List — pay fee with Connected Wallet')}
          </Button>
          <Button
            fullWidth
            onClick={onClose}
            disabled={isProcessing}
            sx={{ mt: 0.5, color: '#8ea3b3', fontWeight: 700 }}
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
